// Content Edit Page
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
} from '@mui/icons-material';

import { useContent, useUpdateContent } from '../hooks/useContent';
import { ContentForm } from '../types';
import MediaFileSelector from '../../../components/MediaFileSelector';
import { useAppSelector } from '../../../store/hooks';

const ContentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { waypoint_name, version } = useParams<{ waypoint_name: string; version: string }>();
  const contentVersion = parseInt(version || '1');

  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User

  // Check if this is a view-only mode (no /edit in URL)
  const isViewMode = !window.location.pathname.includes('/edit');

  // State
  const [contentForm, setContentForm] = useState<ContentForm>({
    waypoint_name: '',
    primary_duration: 60,
    primary_long_text_content: '',
    primary_short_text_content: '',
    primary_image_url: '',
    primary_video_url: '',
    primary_audio_url: '',
    primary_audio_volume: '7',
    primary_genai_context: '',
    primary_optional_text: '',
    primary_display_template: 'TEXT',
    secondary_long_text_content: '',
    secondary_image_url: '',
    secondary_video_url: '',
    secondary_audio_url: '',
    secondary_optional_text: '',
    secondary_display_template: 'TEXT',
  });

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [fileSelectorOpen, setFileSelectorOpen] = useState(false);
  const [fileSelectorType, setFileSelectorType] = useState<'image' | 'video' | 'audio'>('image');
  const [fileSelectorField, setFileSelectorField] = useState<string>('');
  const [fileSelectorScreen, setFileSelectorScreen] = useState<'primary' | 'secondary'>('primary');

  // Hooks
  const { data: contentTemplate, loading: contentLoading } = useContent(waypoint_name || '', contentVersion);
  const updateContent = useUpdateContent();

  // Initialize form when content data loads
  useEffect(() => {
    if (contentTemplate) {
      console.log('DEBUG: contentTemplate loaded:', contentTemplate);
      setContentForm({
        waypoint_name: contentTemplate.waypoint_name || '',
        primary_duration: contentTemplate.primary_duration,
        primary_long_text_content: contentTemplate.primary_long_text_content,
        primary_short_text_content: contentTemplate.primary_short_text_content,
        primary_image_url: contentTemplate.primary_image_url || '',
        primary_video_url: contentTemplate.primary_video_url || '',
        primary_audio_url: contentTemplate.primary_audio_url || '',
        primary_audio_volume: contentTemplate.primary_audio_volume || '7',
        primary_genai_context: contentTemplate.primary_genai_context || '',
        primary_optional_text: contentTemplate.primary_optional_text || '',
        primary_display_template: contentTemplate.primary_display_template,
        secondary_long_text_content: contentTemplate.secondary_long_text_content || '',
        secondary_image_url: contentTemplate.secondary_image_url || '',
        secondary_video_url: contentTemplate.secondary_video_url || '',
        secondary_audio_url: contentTemplate.secondary_audio_url || '',
        secondary_optional_text: contentTemplate.secondary_optional_text || '',
        secondary_display_template: contentTemplate.secondary_display_template || 'TEXT',
      });
    }
  }, [contentTemplate]);

  const handleFileSelect = (fileUrl: string) => {
    setContentForm(prev => ({
      ...prev,
      [fileSelectorField]: fileUrl,
    }));
    setFileSelectorOpen(false);
  };

  const openFileSelector = (type: 'image' | 'video' | 'audio', field: string) => {
    setFileSelectorType(type);
    setFileSelectorField(field);
    setFileSelectorScreen(field.startsWith('secondary') ? 'secondary' : 'primary');
    setFileSelectorOpen(true);
  };

  const handleFormFieldChange = (field: keyof ContentForm, value: any) => {
    setContentForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderImagePreview = (url: string) => {
    if (!url) return null;
    return (
      <Box
        sx={{
          mt: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          maxWidth: 360,
        }}
      >
        <Box
          component="img"
          src={url}
          alt="Selected preview"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    );
  };

  const renderVideoPreview = (url: string) => {
    if (!url) return null;
    return (
      <Box
        sx={{
          mt: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          maxWidth: 480,
        }}
      >
        <Box
          component="video"
          src={url}
          controls
          sx={{ width: '100%', height: 'auto', display: 'block', backgroundColor: 'black' }}
        />
      </Box>
    );
  };

  const handleSave = async () => {
    if (!waypoint_name) return;

    try {
      console.log('DEBUG: Sending update data:', contentForm);
      await updateContent.mutate({
        waypointName: waypoint_name,
        version: contentVersion,
        data: contentForm,
      });
      setSnackbarMessage('Content updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/content');
    } catch (error: any) {
      setSnackbarMessage(error.message || 'Failed to update content');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  if (contentLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contentTemplate) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Content template not found</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/content')}>
          Back to Content List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{isViewMode ? 'View Content' : 'Edit Content'}</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button startIcon={<CancelIcon />} onClick={() => navigate('/content')}>
            {isViewMode ? 'Back to Content' : 'Cancel'}
          </Button>
          {!isViewMode && canEdit && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={updateContent.loading}>
              {updateContent.loading ? 'Updating...' : 'Update Content'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Content Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Content Information
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Waypoint:
            </Typography>
            <Typography variant="h6">{contentForm.waypoint_name}</Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Screen Content - two columns */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Primary Screen */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Primary Screen Content
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Duration (seconds)"
                value={contentForm.primary_duration}
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || value === '0') {
                    handleFormFieldChange('primary_duration', '');
                  } else {
                    const numValue = parseInt(value);
                    if (!isNaN(numValue)) {
                      handleFormFieldChange('primary_duration', numValue);
                    }
                  }
                }}
                sx={{ minWidth: 120 }}
                disabled={isViewMode}
              />
              <FormControl sx={{ minWidth: 120 }} disabled={isViewMode}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={contentForm.primary_display_template}
                  onChange={(e: SelectChangeEvent) => handleFormFieldChange('primary_display_template', e.target.value)}
                  label="Template"
                  disabled={isViewMode}
                >
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="IMAGE">Image</MenuItem>
                  <MenuItem value="VIDEO">Video</MenuItem>
                  <MenuItem value="AUDIO">Audio</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label="Short Text"
              multiline
              rows={2}
              value={contentForm.primary_short_text_content}
              onChange={e => handleFormFieldChange('primary_short_text_content', e.target.value)}
              sx={{ mb: 2 }}
              disabled={isViewMode}
            />

            <TextField
              fullWidth
              label="Long Text"
              multiline
              rows={4}
              value={contentForm.primary_long_text_content}
              onChange={e => handleFormFieldChange('primary_long_text_content', e.target.value)}
              sx={{ mb: 2 }}
              disabled={isViewMode}
            />

            <Typography variant="subtitle1" gutterBottom>
              Media Files:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ImageIcon />
                <TextField
                  label="Image URL"
                  fullWidth
                  value={contentForm.primary_image_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose an image file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('image', 'primary_image_url')}
                  disabled={isViewMode}
                >
                  {contentForm.primary_image_url ? 'Change' : 'Select'}
                </Button>
              </Box>
              {renderImagePreview(contentForm.primary_image_url)}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <VideoIcon />
                <TextField
                  label="Video URL"
                  fullWidth
                  value={contentForm.primary_video_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose a video file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('video', 'primary_video_url')}
                  disabled={isViewMode}
                >
                  {contentForm.primary_video_url ? 'Change' : 'Select'}
                </Button>
              </Box>
              {renderVideoPreview(contentForm.primary_video_url)}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AudioIcon />
                <TextField
                  label="Audio URL"
                  fullWidth
                  value={contentForm.primary_audio_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose an audio file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('audio', 'primary_audio_url')}
                  disabled={isViewMode}
                >
                  {contentForm.primary_audio_url ? 'Change' : 'Select'}
                </Button>
              </Box>
            </Box>

            {/* Audio Volume */}
            <TextField
              label="Audio Volume (0–10)"
              value={contentForm.primary_audio_volume}
              onChange={e => {
                const val = e.target.value;
                if (val === '' || /^([0-9]|10)$/.test(val)) {
                  handleFormFieldChange('primary_audio_volume', val);
                }
              }}
              sx={{ mt: 2, minWidth: 140 }}
              helperText="Playback volume level"
              disabled={isViewMode}
            />

            {/* GenAI Context */}
            <TextField
              fullWidth
              label="GenAI Context"
              multiline
              rows={2}
              value={contentForm.primary_genai_context}
              onChange={e => handleFormFieldChange('primary_genai_context', e.target.value)}
              sx={{ mt: 2 }}
              helperText="Optional context string for GenAI features (leave blank to disable)"
              disabled={isViewMode}
            />
          </CardContent>
        </Card>

        {/* Secondary Screen */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Secondary Screen Content
            </Typography>

            <FormControl sx={{ mb: 2, minWidth: 120 }} disabled={isViewMode}>
              <InputLabel>Template</InputLabel>
              <Select
                value={contentForm.secondary_display_template}
                onChange={(e: SelectChangeEvent) => handleFormFieldChange('secondary_display_template', e.target.value)}
                label="Template"
                disabled={isViewMode}
              >
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="IMAGE">Image</MenuItem>
                <MenuItem value="VIDEO">Video</MenuItem>
                <MenuItem value="AUDIO">Audio</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Long Text"
              multiline
              rows={4}
              value={contentForm.secondary_long_text_content}
              onChange={e => handleFormFieldChange('secondary_long_text_content', e.target.value)}
              sx={{ mb: 2 }}
              disabled={isViewMode}
            />

            <Typography variant="subtitle1" gutterBottom>
              Media Files:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ImageIcon />
                <TextField
                  label="Image URL"
                  fullWidth
                  value={contentForm.secondary_image_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose an image file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('image', 'secondary_image_url')}
                  disabled={isViewMode}
                >
                  {contentForm.secondary_image_url ? 'Change' : 'Select'}
                </Button>
              </Box>
              {renderImagePreview(contentForm.secondary_image_url)}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <VideoIcon />
                <TextField
                  label="Video URL"
                  fullWidth
                  value={contentForm.secondary_video_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose a video file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('video', 'secondary_video_url')}
                  disabled={isViewMode}
                >
                  {contentForm.secondary_video_url ? 'Change' : 'Select'}
                </Button>
              </Box>
              {renderVideoPreview(contentForm.secondary_video_url)}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AudioIcon />
                <TextField
                  label="Audio URL"
                  fullWidth
                  value={contentForm.secondary_audio_url}
                  InputProps={{ readOnly: true }}
                  helperText="Use the Select button to choose an audio file"
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => openFileSelector('audio', 'secondary_audio_url')}
                  disabled={isViewMode}
                >
                  {contentForm.secondary_audio_url ? 'Change' : 'Select'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Media File Selector */}
      <MediaFileSelector
        open={fileSelectorOpen}
        onClose={() => setFileSelectorOpen(false)}
        onSelect={handleFileSelect}
        fileType={fileSelectorType}
        contentId={contentTemplate?.id}
        screen={fileSelectorScreen}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentEditPage;
